import React, { useEffect, useState } from 'react';
import { Box, TextField, MenuItem, FormHelperText } from '@mui/material';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const CURRENT_YEAR = new Date().getFullYear();
// Show years from 1920 to current year
const YEARS = Array.from({ length: CURRENT_YEAR - 1919 }, (_, i) => CURRENT_YEAR - i);

function daysInMonth(month: number, year: number): number {
  if (!month || !year) return 31;
  return new Date(year, month, 0).getDate();
}

function parseValue(value: string): { year: string; month: string; day: string } {
  if (!value) return { year: '', month: '', day: '' };
  const parts = value.split('-');
  return {
    year: parts[0] || '',
    month: parts[1] || '',
    day: parts[2] || '',
  };
}

interface DobPickerProps {
  /** Controlled value in "YYYY-MM-DD" format. Empty string when nothing selected. */
  value: string;
  onChange: (val: string) => void;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  label?: string;
}

/**
 * Date-of-birth picker with separate Day / Month / Year dropdowns.
 * Partial selections are kept locally until all three parts are set.
 */
const DobPicker: React.FC<DobPickerProps> = ({
  value,
  onChange,
  error = false,
  helperText,
  required = false,
  label = 'Date of Birth',
}) => {
  const [year, setYear] = useState(() => parseValue(value).year);
  const [month, setMonth] = useState(() => parseValue(value).month);
  const [day, setDay] = useState(() => parseValue(value).day);

  // Sync from parent when a full date is set/cleared externally (e.g. form reset)
  useEffect(() => {
    const next = parseValue(value);
    if (value) {
      setYear(next.year);
      setMonth(next.month);
      setDay(next.day);
    } else if (!year && !month && !day) {
      // already empty locally
    } else if (year && month && day) {
      // Parent cleared a previously complete date
      setYear('');
      setMonth('');
      setDay('');
    }
    // Intentionally ignore incomplete local drafts when parent value is still ''
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const numYear = parseInt(year, 10);
  const numMonth = parseInt(month, 10);
  const maxDays = numYear && numMonth ? daysInMonth(numMonth, numYear) : 31;
  const days = Array.from({ length: maxDays }, (_, i) => i + 1);

  const emit = (y: string, m: string, d: string) => {
    setYear(y);
    setMonth(m);
    setDay(d);

    if (y && m && d) {
      const max = daysInMonth(parseInt(m, 10), parseInt(y, 10));
      const safeDay = Math.min(parseInt(d, 10), max);
      const paddedDay = String(safeDay).padStart(2, '0');
      if (paddedDay !== d) setDay(paddedDay);
      onChange(`${y}-${m.padStart(2, '0')}-${paddedDay}`);
    } else {
      // Keep day/month/year draft in local state; only clear parent ISO value
      onChange('');
    }
  };

  return (
    <Box sx={{ mt: 2, mb: 0, width: '100%', gridColumn: '1 / -1' }}>
      <Box
        component="fieldset"
        sx={{
          border: 'none',
          p: 0,
          m: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          width: '100%',
        }}
      >
        <Box
          component="legend"
          sx={{
            fontSize: '0.875rem',
            color: error ? 'error.main' : 'text.secondary',
            mb: 1,
            pl: 0.25,
          }}
        >
          {label}{required && ' *'}
        </Box>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'minmax(88px, 1fr) minmax(160px, 2fr) minmax(110px, 1.25fr)',
            },
            gap: 1.5,
            width: '100%',
          }}
        >
          <TextField
            select
            label="Day"
            value={day}
            onChange={(e) => emit(year, month, e.target.value)}
            required={required}
            error={error}
            fullWidth
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value=""><em>Day</em></MenuItem>
            {days.map((d) => (
              <MenuItem key={d} value={String(d).padStart(2, '0')}>
                {d}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Month"
            value={month}
            onChange={(e) => emit(year, e.target.value, day)}
            required={required}
            error={error}
            fullWidth
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value=""><em>Month</em></MenuItem>
            {MONTHS.map((m, i) => (
              <MenuItem key={i + 1} value={String(i + 1).padStart(2, '0')}>
                {m}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Year"
            value={year}
            onChange={(e) => emit(e.target.value, month, day)}
            required={required}
            error={error}
            fullWidth
            SelectProps={{
              displayEmpty: true,
              MenuProps: {
                PaperProps: { style: { maxHeight: 280 } },
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
              },
            }}
          >
            <MenuItem value=""><em>Year</em></MenuItem>
            {YEARS.map((y) => (
              <MenuItem key={y} value={String(y)}>
                {y}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>

      {helperText && (
        <FormHelperText error={error} sx={{ ml: 0.25, mt: 0.75 }}>
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
};

export default DobPicker;
